import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import * as Sentry from "@sentry/react";
import { User } from "@/types/Users";
import { AuthState } from "@/types/Auth";
import Cookies from "js-cookie";
import { isUnifiedAccessEnabled } from "@/lib/unified-access";
import { authApi } from "@/lib/api/auth";
import {
  fetchSessionAccount,
  updateSessionAccount,
  fetchMyClient,
  fetchUserLocations,
  updateLastPage,
} from "@/lib/api/web-api-client";
import { isTransportFailure } from "@/lib/api/http-error";
import { useItemsStore } from "./items";

// Lazy import db to avoid Edge Runtime issues in middleware
// db uses Dexie (IndexedDB) which requires browser APIs
let db: any = null;
async function getDb() {
  if (typeof window === "undefined") return null; // Not in browser
  if (db) return db;

  const { db: importedDb } = await import("@/lib/inspections/db");
  db = importedDb;
  return db;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      users: [],
      isAuthenticated: false,
      isLoading: false,
      lastPage: null,
      error: null,
      verificationEmail: null,
      verifyLastSent: null,
      // Auth Actions
      setUser: (user: User | null) => set({ user }),
      setAuthenticated: (status) => set({ isAuthenticated: status }),
      setLoading: (status) => set({ isLoading: status }),
      setError: (error: string | null) => set({ error }),
      setVerificationEmail: (email: string) =>
        set({ verificationEmail: email }),
      setVerifyLastSent: (timestamp: number) =>
        set({ verifyLastSent: timestamp }),
      acceptInvite: async (email: string, password: string, token: string) => {
        await authApi.acceptInvite(email, password, token);
      },
      forgotPassword: async (email: string, url: string) => {
        return await authApi.requestPasswordReset(email, url);
      },
      setPassword: async (password: string, token: string) => {
        return await authApi.setPassword(password, token);
      },
      // Token Management
      setAccessToken: (token, expires) => {
        const e = expires ? Date.now() + expires : Date.now() + 900000;
        if (token) {
          // Cookie storage (existing - primary)
          Cookies.set("access_token", token, {
            expires: new Date(e),
            path: "/",
          });
          Cookies.set("token_expires", e.toString(), {
            expires: new Date(e),
            path: "/",
          });

          // IndexedDB storage (NEW - backup for iOS PWA scope changes)
          // Only run in browser context (not in middleware/Edge Runtime)
          getDb().then((db) => {
            if (!db) return;
            db.auth_tokens
              .put({
                key: "access_token",
                value: token,
                updated_at: new Date(),
              })
              .catch((err: Error) => {
                console.error(
                  "[Auth] Failed to store access_token in IndexedDB:",
                  err,
                );
                Sentry.captureException(err, {
                  tags: { area: "auth", stage: "indexeddb-write" },
                });
              });
            db.auth_tokens
              .put({
                key: "token_expires",
                value: e.toString(),
                updated_at: new Date(),
              })
              .catch((err: Error) => {
                console.error(
                  "[Auth] Failed to store token_expires in IndexedDB:",
                  err,
                );
                Sentry.captureException(err, {
                  tags: { area: "auth", stage: "indexeddb-write" },
                });
              });
          });
        } else {
          Cookies.remove("access_token", { path: "/" });
          Cookies.remove("token_expires", { path: "/" });

          // Remove from IndexedDB too
          getDb().then((db) => {
            if (!db) return;
            db.auth_tokens.delete("access_token").catch(() => {});
            db.auth_tokens.delete("token_expires").catch(() => {});
          });
        }
      },
      setRefreshToken: (token) => {
        if (token) {
          // Cookie storage (existing - primary)
          Cookies.set("refresh_token", token, {
            expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            path: "/",
          });

          // IndexedDB storage (NEW - backup for iOS PWA scope changes)
          // Only run in browser context (not in middleware/Edge Runtime)
          getDb().then((db) => {
            if (!db) return;
            db.auth_tokens
              .put({
                key: "refresh_token",
                value: token,
                updated_at: new Date(),
              })
              .catch((err: Error) => {
                console.error(
                  "[Auth] Failed to store refresh_token in IndexedDB:",
                  err,
                );
                Sentry.captureException(err, {
                  tags: { area: "auth", stage: "indexeddb-write" },
                });
              });
          });
        } else {
          Cookies.remove("refresh_token", { path: "/" });

          // Remove from IndexedDB too
          getDb().then((db) => {
            if (!db) return;
            db.auth_tokens.delete("refresh_token").catch(() => {});
          });
        }
      },
      getAccessToken: () => ({
        accessToken: Cookies.get("access_token"),
        tokenExpires: Number(Cookies.get("token_expires")),
      }),
      getRefreshToken: () => Cookies.get("refresh_token"),
      // Page Management
      setLastPage: (page: string) => {
        Cookies.set("last_page", page, {
          expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          path: "/",
        });
        set({ lastPage: page });
      },
      storeLastPage: (page: string) => {
        const user = get().user;
        if (user?.id) {
          // Best effort: without a connection the write is skipped, and the
          // next page view online stores the page again.
          updateLastPage(page).catch((error: unknown) => {
            if (isTransportFailure(error)) return;
            throw error;
          });
        }
      },
      // Auth Flow
      logout: async (callbackPath?: string | null) => {
        const { isAuthenticated, clearCookies } = get();

        try {
          if (!isAuthenticated) return;

          const refreshToken = Cookies.get("refresh_token");
          if (refreshToken) {
            await authApi.logout(refreshToken);
          }
        } finally {
          clearCookies(callbackPath);
        }
      },
      // The application auth state includes role, metadata, provider,
      // contract types and title in addition to the session identity fields.
      getMe: async (options) => {
        const { isAuthenticated } = get();
        if (!isAuthenticated) return;
        try {
          const user = await fetchSessionAccount();
          set({ user });

          if (user.role) {
            useItemsStore.getState().setSelectedItemId("user_role", user.role);
          }
          // The remembered provider feeds the legacy login shortcut only; the
          // email-first login asks the address's domain instead.
          if (user.provider && !isUnifiedAccessEnabled()) {
            Cookies.set("auth_provider", user.provider, {
              expires: 365,
              path: "/",
            });
          }
          if (user.client_id) {
            const client = await fetchMyClient();
            if (client?.sifa_id) {
              useItemsStore
                .getState()
                .setSelectedItemId("sifa", client.sifa_id);
            }
            if (client?.ama_id) {
              useItemsStore.getState().setSelectedItemId("ama", client.ama_id);
            }
          }
          const locations = await fetchUserLocations({ userId: user.id });
          if (locations?.length > 0) {
            const l = locations
              .map((assignment) => assignment.location.id)
              .join(",");
            useItemsStore.getState().setSelectedItemId("location", l);
          }
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to fetch user profile",
          });
          // Most callers treat a stale session as recoverable and swallow
          // this. `updateMe`'s own read-back opts in to rethrow instead: a
          // failed refetch there must fail the save, not silently keep the
          // pre-save profile while the caller reports success.
          if (options?.rethrow) throw error;
        }
      },
      updateMe: async (data) => {
        const { isAuthenticated } = get();
        if (!isAuthenticated) return;

        const id = get().user?.id;

        if (!id) throw new Error("User not found");

        try {
          await updateSessionAccount(data);
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to update user profile",
          });
          // The caller shows the outcome; a swallowed failure here reads as
          // a successful save whose fields then revert on the next render.
          throw error;
        }
        // Reload the full account, including routing and provider fields,
        // after a profile write. A failed reload must reach the caller so a
        // successful save cannot leave a silently stale session.
        await get().getMe({ rethrow: true });
      },
      initializeAuth: async () => {
        const { getAccessToken, logout } = get();
        const accessToken = getAccessToken();

        if (!accessToken) {
          await logout();
          return;
        }

        const state = get();
        set({ isLoading: true });
        if (!state.user?.id) {
          try {
            const user = await fetchSessionAccount();
            set({ user });
          } catch (error) {
            set({
              error:
                error instanceof Error
                  ? error.message
                  : "Failed to initialize auth",
            });
          } finally {
            set({ isLoading: false });
          }
        }
      },
      clearCookies: (callbackPath?: string | null) => {
        Cookies.remove("access_token", { path: "/" });
        Cookies.remove("token_expires", { path: "/" });
        Cookies.remove("refresh_token", { path: "/" });
        Cookies.remove("last_page", { path: "/" });

        // Clear IndexedDB tokens too (only in browser)
        getDb().then((db) => {
          if (!db) return;
          db.auth_tokens.delete("access_token").catch(() => {});
          db.auth_tokens.delete("refresh_token").catch(() => {});
          db.auth_tokens.delete("token_expires").catch(() => {});
        });

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          users: [],
        });

        // Preserve callback for all protected routes (except auth pages)
        // null = explicit logout (don't preserve), undefined = auto-preserve, string = use provided path
        // Auth interceptors supply the current pathname without its query.
        const currentPath = window.location.pathname;
        const redirectPath =
          callbackPath === null
            ? null
            : !callbackPath || callbackPath === currentPath
              ? currentPath + window.location.search
              : callbackPath;
        const shouldPreserveCallback =
          redirectPath &&
          !redirectPath.startsWith("/auth") &&
          redirectPath.split(/[?#]/)[0] !== "/";

        // A preserved callback resumes in the same role context after sign-in.
        if (!shouldPreserveCallback) {
          useItemsStore.getState().clearCollection("user_role");
          useItemsStore.getState().clearCollection("sifa");
          useItemsStore.getState().clearCollection("ama");
          useItemsStore.getState().clearCollection("location");
          useItemsStore.getState().clearCollection("fetch_course_shares");
        }

        set({ user: null });
        set({ verificationEmail: null });
        set({ verifyLastSent: null });

        if (shouldPreserveCallback) {
          window.location.href = `/auth/login?callback=${encodeURIComponent(redirectPath)}`;
        } else {
          window.location.href = "/auth/login";
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        verificationEmail: state.verificationEmail,
        verifyLastSent: state.verifyLastSent,
      }),
    },
  ),
);
